import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AssigneeMenuContent } from "@/components/invoices/AssigneeMenuContent";
import { UserAvatar } from "@/components/common/UserAvatar";
import { cn } from "@/lib/utils";

/**
 * The assignee cell: the assigned person's picture beside their name.
 *
 * This was a native `<select>`, which cannot render an image in an option — the
 * element only accepts text. Showing a face therefore requires a real menu, so
 * this is a dropdown whose trigger doubles as the current-assignee display.
 *
 * Assignment is keyed on the user's guid because that is what
 * `POST /invoices/:guid:assign-to` expects. The old app kept a numeric id in the
 * filter modal and a guid in the assign modal, and converted between them by
 * name lookup.
 */
export function InvoiceAssigneeSelect({
  invoice,
  assignees = [],
  onChange,
  onRequestAssignees,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const value = invoice.assignedToGuid ?? "";
  const assigned = (assignees ?? []).find((user) => user?.guid === value);

  // The row may be assigned to someone outside the list this queue loaded (My
  // Invoices filters the viewer out), so fall back to the name on the invoice.
  const assignedName = assigned?.name ?? invoice.assignedTo ?? null;
  const stop = (event) => event.stopPropagation();

  const select = (guid) => {
    setOpen(false);
    onChange?.(invoice, guid);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (next) onRequestAssignees?.();
        setOpen(next);
      }}
    >
      <DropdownMenuTrigger
        disabled={disabled}
        onClick={stop}
        onMouseDown={stop}
        onPointerEnter={onRequestAssignees}
        onFocus={onRequestAssignees}
        aria-label={
          assignedName
            ? `Assigned to ${assignedName}. Reassign invoice ${invoice.invoiceNumber}.`
            : `Assign invoice ${invoice.invoiceNumber}`
        }
        className={cn(
          "invoice-assignee-trigger",
          value && "invoice-assignee-trigger-assigned",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        {value ? (
          <>
            <UserAvatar user={assigned ?? { name: assignedName }} guid={value} size="sm" />
            <span className="invoice-assignee-name">{assignedName}</span>
          </>
        ) : (
          <span className="invoice-assignee-placeholder">Assign…</span>
        )}
        <ChevronDown className="invoice-assignee-caret" aria-hidden="true" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-60 p-1">
        <AssigneeMenuContent
          assignees={assignees}
          selectedGuid={value || null}
          onSelect={select}
          onUnassign={value ? () => select(null) : undefined}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
